import { z } from 'zod';
import { FormStrategy } from './form-learnings';

export const EvaluateJobRequest = z.object({
  jobContext: z.object({
    title: z.string().default(''),
    company: z.string().default(''),
    location: z.string().optional(),
    // `description` is the meaty field for fit evaluation but the right
    // panel sometimes loads partially. Permissive `.default('')` lets
    // the server still answer (with low confidence) rather than 400 the
    // entire request — the LLM will say "not enough info" honestly.
    // Hard cap at 20KB so a poisoned job posting can't blow the prompt
    // budget or smuggle in a multi-thousand-line jailbreak payload.
    description: z.string().max(20_000).default(''),
    salaryMin: z.number().optional(),
    salaryMax: z.number().optional(),
    workplaceType: z.string().optional(),
  }),
});
export type EvaluateJobRequest = z.infer<typeof EvaluateJobRequest>;

export const EvaluateJobResponse = z.object({
  fit: z.boolean(),
  reason: z.string(),
  confidence: z.number().min(0).max(1),
  scores: z
    .object({
      title: z.number().min(0).max(1),
      seniority: z.number().min(0).max(1),
      compensation: z.number().min(0).max(1),
      location: z.number().min(0).max(1),
    })
    .optional(),
});
export type EvaluateJobResponse = z.infer<typeof EvaluateJobResponse>;

export const AnswerQuestionRequest = z.object({
  // Hard 1000-char cap on the question text. Form-field labels are
  // measured in dozens of chars; anything larger is either a buggy ATS
  // dumping its whole form into one label or a prompt-injection probe.
  question: z.string().max(1000),
  jobContext: z
    .object({
      title: z.string(),
      company: z.string(),
    })
    .optional(),
  fieldType: z.enum(['text', 'textarea', 'select', 'radio', 'checkbox', 'number']).optional(),
  options: z.array(z.string()).optional(),
  /**
   * ATS scope for the cross-user form-learnings lookup. Same contract as
   * AnswerBatchRequest — when present the server injects strategies from
   * the form-learnings DDB scoped to this (atsType, atsDomain) pair, so
   * the per-question handlers (Ashby, Lever, Workday, external-apply)
   * get the same learnings boost the batch path already gets.
   */
  atsType: z.string().optional(),
  atsDomain: z.string().optional(),
});
export type AnswerQuestionRequest = z.infer<typeof AnswerQuestionRequest>;

export const AnswerQuestionResponse = z.object({
  answer: z.string(),
  confidence: z.number().min(0).max(1),
  reasoning: z.string().optional(),
  /**
   * Set by the model router when even a retry on a stronger model
   * couldn't push confidence above the threshold. Downstream code
   * (worker, needs-attention pipeline) treats this as a soft "ask a
   * human" signal without blocking the apply flow.
   */
  lowConfidence: z.boolean().optional(),
  /** Which model produced this answer — surfaced for cost/accuracy analysis. */
  model: z.string().optional(),
  /** True if the answer is the result of a confidence-driven retry. */
  retried: z.boolean().optional(),
  /**
   * True when the answer was served from the per-user QA cache (#331)
   * instead of a fresh LLM call. Surfaces cache-hit rates to admin
   * dashboards and to per-cycle worker telemetry without forcing a
   * separate observability channel.
   */
  cached: z.boolean().optional(),
});
export type AnswerQuestionResponse = z.infer<typeof AnswerQuestionResponse>;

export const ParseResumeRequest = z.object({
  s3Key: z.string().min(1),
});
export type ParseResumeRequest = z.infer<typeof ParseResumeRequest>;

export const ParseResumeResponse = z.object({
  version: z.number().int(),
  parsedMetadata: z.record(z.string(), z.unknown()),
});
export type ParseResumeResponse = z.infer<typeof ParseResumeResponse>;

/**
 * Generic prompt-in / text-out completion request. Used by the worker
 * modules (form-recovery, form-learnings, batch-answerer, visual-navigator)
 * that need a free-form prompt endpoint with optional image inputs.
 */
export const CompleteRequest = z.object({
  // Cap the free-form prompt at 30KB. The escape-hatch route forwards
  // straight to the provider; an unbounded prompt is a DoS + cost-burn
  // surface. The worker modules that hit this route (form-recovery,
  // form-learnings, batch-answerer, visual-navigator) never need more.
  prompt: z.string().min(1).max(30_000),
  /** Base64-encoded PNG screenshots (no `data:` prefix). */
  images: z.array(z.string()).optional(),
  /** When true, request JSON-mode output from the provider. */
  json: z.boolean().optional(),
  maxTokens: z.number().int().positive().optional(),
});
export type CompleteRequest = z.infer<typeof CompleteRequest>;

export const CompleteResponse = z.object({
  content: z.string(),
  usage: z
    .object({
      promptTokens: z.number().int().nonnegative(),
      completionTokens: z.number().int().nonnegative(),
      totalTokens: z.number().int().nonnegative(),
    })
    .optional(),
});
export type CompleteResponse = z.infer<typeof CompleteResponse>;

/* ------------------------------------------------------------------ *
 * answer-batch — multi-question form answers in one round-trip.
 *
 * Replaces the legacy worker's prompt-string-builder in
 * applicator/batch-answerer.ts. Server holds the system prompt + the
 * answer-bank/sample-answer merging logic; worker just sends the field
 * list + an optional screenshot for visual context.
 * ------------------------------------------------------------------ */

export const BatchAnswerField = z.object({
  /** Sparse 1-based index the worker uses to bind answers back to DOM elements. */
  index: z.number().int().positive(),
  /** Visible question label / field name the user would read. */
  question: z.string(),
  /** Field affordance — drives whether we expect a free-form or constrained answer. */
  fieldType: z.enum(['text', 'textarea', 'select', 'radio', 'checkbox', 'number']).optional(),
  /** For select/radio/checkbox: the available options the LLM must pick from. */
  options: z.array(z.string()).optional(),
  /** Optional pre-existing value (e.g., the field already had a prefill). */
  currentValue: z.string().optional(),
});
export type BatchAnswerField = z.infer<typeof BatchAnswerField>;

export const AnswerBatchRequest = z.object({
  fields: z.array(BatchAnswerField).min(1).max(50),
  jobContext: z.object({ title: z.string(), company: z.string() }).optional(),
  /** Optional base64-encoded PNG screenshot for visual context. */
  screenshot: z.string().optional(),
  /**
   * ATS provider — 'greenhouse' | 'ashby' | 'lever' | 'workday' | 'custom'
   * etc. Drives the server-side learnings lookup so the form-fill prompt
   * gets the top-N strategies the cross-user corpus has accumulated for
   * THIS ATS. Without it, the LLM answers field labels with no prior
   * knowledge of what works on this surface.
   */
  atsType: z.string().optional(),
  /**
   * Hostname of the page the worker is filling. Pairs with atsType to
   * narrow the learnings query down to (atsType, atsDomain) — e.g. an
   * Ashby form on `jobs.ashbyhq.com/recharge` gets recharge-specific
   * strategies if any have been learned.
   */
  atsDomain: z.string().optional(),
});
export type AnswerBatchRequest = z.infer<typeof AnswerBatchRequest>;

export const BatchAnswer = z.object({
  index: z.number().int(),
  answer: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
});
export type BatchAnswer = z.infer<typeof BatchAnswer>;

/**
 * Per-call usage block. Mirrors the shape `complete` already returns.
 * Optional so old client builds parsing this schema don't break, but
 * the cloud route always populates it now — propagating real token
 * counts so the worker's per-cycle `[batch-answerer] (N tokens)` log
 * stops lying with "0".
 */
export const LlmUsage = z.object({
  inputTokens: z.number().int().min(0),
  outputTokens: z.number().int().min(0),
  totalTokens: z.number().int().min(0),
});
export type LlmUsage = z.infer<typeof LlmUsage>;

export const AnswerBatchResponse = z.object({
  answers: z.array(BatchAnswer),
  usage: LlmUsage.optional(),
});
export type AnswerBatchResponse = z.infer<typeof AnswerBatchResponse>;

/* ------------------------------------------------------------------ *
 * recover-form — vision-based form fix advice.
 *
 * Replaces applicator/form-recovery.ts. Worker uploads screenshot(s)
 * and error context; server returns a list of typed recovery actions.
 * ------------------------------------------------------------------ */

/**
 * Per-action metadata the cloud LLM emits to short-circuit the worker's
 * execution path. These flags encode the legacy `executeRecoveryActions`
 * decision-tree branches that used to live in `apps/legacy` form-recovery —
 * they're now produced by the model so the worker can stay thin.
 *
 * - `isIdentityField`: marks a Name/Email/Phone/SSN target the worker must
 *   not overwrite if an existing value is present. Legacy lines 1504-1507.
 * - `isDateOnlyLabel`: target is a bare "From"/"To"/"Date" label whose
 *   substring-greedy selectors would land on an unrelated identity field
 *   (e.g. a LinkedIn URL placeholder). Legacy lines 1452-1462.
 * - `dropdownAttemptedButUncommitted`: the model believes the dropdown
 *   handler opened but failed to commit a value — worker must NOT count
 *   the open-but-empty state as success. Legacy lines 988-995.
 */
export const RecoverFormActionMeta = z
  .object({
    isIdentityField: z.boolean().optional(),
    isDateOnlyLabel: z.boolean().optional(),
    dropdownAttemptedButUncommitted: z.boolean().optional(),
  })
  .optional();
export type RecoverFormActionMeta = z.infer<typeof RecoverFormActionMeta>;

/**
 * Recovery-action contract. The unified shape that the legacy 1100-line
 * `executeRecoveryActions` decision tree collapses into.
 *
 * The `action` enum keeps the original five verbs for backward compatibility
 * and adds five new dispatcher targets the worker handles:
 *
 *   - `press-sequentially` — anti-bot slow-typing via `pressSequentially`.
 *     Legacy 1620-1644. Server-side antibot scoring (Ashby SPAM_FLAGGED)
 *     reads per-keystroke input events; instant `.fill()` is a strong bot
 *     signal. The model emits this verb whenever it expects a human-paced
 *     keystroke sequence (combobox + slow-typing).
 *   - `dropdown-select` — opens a custom dropdown, searches for the option,
 *     verifies via `stillUnset()` then retries via type+Enter. Legacy
 *     997-1250. Covers React-Select, Greenhouse Country, Workday
 *     promptOption, Ashby fuzzy-option matching with META_WORDS guard.
 *   - `date-triplet` — Workday MM/DD/YYYY 3-input widget. Routes through
 *     `dateSectionMonth/Day/Year-input` instead of typing into a single
 *     spinbutton. Legacy 1663-1712.
 *   - `needs-attention` — signal that no automated action can succeed;
 *     emit a needs_attention progress event and exit the rescue loop so
 *     the user can step in.
 *   - `diagnose` — last-resort observation call. The model asks for a
 *     human-readable describe-the-failure log so the user gets visibility
 *     into why the runner bailed. Legacy 649-674 `diagnoseAndDump`.
 */
export const RecoverFormAction = z.object({
  action: z.enum([
    'click',
    'fill',
    'select',
    'scroll',
    'skip',
    'press-sequentially',
    'dropdown-select',
    'date-triplet',
    'needs-attention',
    'diagnose',
  ]),
  target: z.string(),
  value: z.string().optional(),
  explanation: z.string(),
  /**
   * Reason the model returned `skip`. Populated when `action === 'skip'`
   * (the cloud route's normalizer encourages it) so the worker can log
   * the SSO / Forgot-password / Bare-target reason verbatim. Free-form
   * but capped at 200 chars so a malicious model can't dump payload here.
   */
  skipReason: z.string().max(200).optional(),
  /**
   * Confidence in [0, 100]. Worker uses this to rank competing
   * candidates when multiple elements match the target text (apply-button
   * discrimination, legacy 1392-1402). Absent → treat as 50.
   */
  confidence: z.number().int().min(0).max(100).optional(),
  /** Per-action meta — see RecoverFormActionMeta. */
  meta: RecoverFormActionMeta,
});
export type RecoverFormAction = z.infer<typeof RecoverFormAction>;

export const RecoverFormRequest = z.object({
  /** Base64-encoded PNG screenshots (no `data:` prefix). One or more — top-to-bottom slices. */
  screenshots: z.array(z.string()).min(1).max(10),
  /**
   * Free-form error context the worker collected (validation messages,
   * exception text). Hard cap at 2000 chars — real ATS error banners
   * are short; anything longer is either a runaway dump or a
   * prompt-injection probe trying to re-open a fake `<user-context>`
   * block via the unfenced concatenation legacy paths used.
   */
  errorContext: z.string().max(2000),
  jobContext: z.object({ title: z.string(), company: z.string() }).optional(),
  /**
   * ATS provider hint so the server can pull cross-user strategies for
   * this surface. Legacy injected `getLearnings()` text directly into
   * the recovery prompt; unified moved the injection server-side and
   * needs (atsType, atsDomain) to do the lookup.
   */
  atsType: z.string().optional(),
  atsDomain: z.string().optional(),
  /**
   * Trimmed HTML of the stuck form (≤50KB). The screenshot tells the
   * LLM what the form LOOKS like; the DOM lets it write precise
   * label-text / selector hooks instead of guessing from the visual.
   * Legacy recovery always sent both — only the screenshot survived the
   * unified migration, which is why the recovery LLM was inventing
   * plausible-but-wrong selectors. Optional + size-capped so
   * screenshots-only callers still work and we never blow the prompt
   * budget on a giant page.
   */
  domHtml: z.string().max(60_000).optional(),
});
export type RecoverFormRequest = z.infer<typeof RecoverFormRequest>;

export const RecoverFormResponse = z.object({
  actions: z.array(RecoverFormAction),
});
export type RecoverFormResponse = z.infer<typeof RecoverFormResponse>;

/* ------------------------------------------------------------------ *
 * analyze-form — capture reusable Playwright strategies from a failed form.
 *
 * Replaces applicator/form-learnings.ts. Pure text-in/JSON-out — no
 * screenshots since the DOM HTML carries the structure.
 * ------------------------------------------------------------------ */

// FormStrategy is defined in `form-learnings.ts` as the canonical
// cross-user shape. AnalyzeFormResponse uses that same type so a worker
// answer drops straight into the shared store without conversion.

export const AnalyzeFormRequest = z.object({
  /**
   * Truncated HTML dump of the broken form. Hard 30KB cap at the schema
   * boundary — the server still slices to 25KB before calling the LLM,
   * but the schema cap rejects multi-megabyte payloads before they ever
   * touch the handler so a malicious worker can't DoS the route.
   */
  html: z.string().min(1).max(30_000),
  atsType: z.string().optional(),
  /** Renamed from `domain` to match the cross-user form-learnings entity. */
  atsDomain: z.string().optional(),
  failureType: z.string().optional(),
  /** Strategies the worker already knows about — let the LLM avoid duplicates. */
  existingStrategies: z.array(z.string()).optional(),
});
export type AnalyzeFormRequest = z.infer<typeof AnalyzeFormRequest>;

export const AnalyzeFormResponse = z.object({
  strategies: z.array(FormStrategy),
});
export type AnalyzeFormResponse = z.infer<typeof AnalyzeFormResponse>;

/* ------------------------------------------------------------------ *
 * navigate-page — vision-based "what should I click to find the form?"
 *
 * Replaces applicator/visual-navigator.ts. Single screenshot → typed
 * navigation instruction.
 * ------------------------------------------------------------------ */

export const NavigatePageRequest = z.object({
  /** Base64-encoded PNG screenshot of the current viewport. */
  screenshot: z.string(),
  // Job titles top out around 100 chars in the wild; 200 leaves headroom
  // without giving a prompt-injection payload room to live.
  jobTitle: z.string().max(200),
  /** Optional context — what the worker has already tried. Capped to 1000 chars. */
  context: z.string().max(1000).optional(),
});
export type NavigatePageRequest = z.infer<typeof NavigatePageRequest>;

export const NavigatePageResponse = z.object({
  action: z.enum(['click', 'scroll', 'none']),
  target: z.string(),
  explanation: z.string(),
  isApplicationForm: z.boolean(),
});
export type NavigatePageResponse = z.infer<typeof NavigatePageResponse>;
